import { useState, useEffect } from "react";
import Accordion from "react-bootstrap/Accordion";
import "./PGFaq.scss";
import AddFaqs from "../../default-hooks/AddFaqs";
import { useCollection } from "../../firebase-files/hooks/useCollection";
import { useFirestore } from "../../firebase-files/hooks/useFirestore";
import { useAuthContext } from "../../firebase-files/hooks/useAuthContext";
import { useDocument } from "../../firebase-files/hooks/useDocument";

const PGFaqs = () => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showAll, setShowAll] = useState({});
  const [toast, setToast] = useState({ show: false, message: "" });
  const [appType, setAppType] = useState("lite");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // ✅ Firestore collections
  const { documents: categories } = useCollection("m_categories", [
    "source",
    "==",
    "faq",
  ]);
  const { documents: faqs } = useCollection("faqs");
  const { documents: users } = useCollection("users");
  const { document: appTypeDoc } = useDocument("settings", "AppType");

  const { deleteDocument: deleteCategory } = useFirestore("m_categories");
  const { deleteDocument: deleteFaq } = useFirestore("faqs");

  const { user } = useAuthContext();

  // ✅ check if current user is admin or superAdmin
  useEffect(() => {
    if (!user || !users) {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      return;
    }

    const phone = user?.phoneNumber?.replace("+91", "91");
    const userInfo = users.find((u) => u.id === phone);

    if (userInfo) {
      setIsAdmin(userInfo.role?.toLowerCase() === "admin");
      setIsSuperAdmin(userInfo.role?.toLowerCase() === "superadmin");
    } else {
      setIsAdmin(false);
      setIsSuperAdmin(false);
    }
  }, [user, users]);

  // ✅ Get apptype from appTypeDoc
  useEffect(() => {
    if (appTypeDoc) {
      // Check for both AppType and apptype fields
      if (appTypeDoc.AppType) {
        setAppType(appTypeDoc.AppType);
      } else if (appTypeDoc.apptype) {
        setAppType(appTypeDoc.apptype);
      }
    }
  }, [appTypeDoc]);

  // ✅ Access rules
  const canAdd = () => {
    if (!user) return false;
    
    if (appType === "pro") {
      return isAdmin || isSuperAdmin;
    } else {
      return isSuperAdmin;
    }
  };

  const canDelete = () => {
    if (!user) return false;
    return isSuperAdmin;
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const toggleShowAll = (categoryId) => {
    setShowAll((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => {
      setToast({ show: false, message: "" });
    }, 3000);
  };

  // ✅ Category delete
  const handleDeleteCategory = async (id) => {
    if (!canDelete()) return;
    if (window.confirm("Are you sure you want to delete this category?")) {
      await deleteCategory(id);
      showToast("Category Deleted Successfully ✅");
    }
  };

  // ✅ FAQ delete
  const handleDeleteFaq = async (id) => {
    if (!canDelete()) return;
    if (window.confirm("Are you sure you want to delete this FAQ?")) {
      await deleteFaq(id);
      showToast("FAQ Deleted Successfully ✅");
    }
  };

  return (
    <section className="eco_faq_section">
      <div className="container">
        <div className="row align-items-center">
          {/* FAQ Content Section */}
          <div className="col-lg-6 col-md-12">
            <div className="faq_content">
              <h2 className="faq_title">Frequently Asked Questions</h2>
              <p className="faq_desc">
                Our objective is to provide the freshest and most beautiful
                flowers for every occasion, ensuring timely delivery, elegant
                presentation, and memorable experiences for our customers.
              </p>

              <div className="faq-categories">
                {categories &&
                  categories.map((category) => {
                    const categoryFaqs = faqs
                      ? faqs.filter(
                          (f) =>
                            f.category?.toLowerCase() ===
                            category.category?.toLowerCase()
                        )
                      : [];

                    if (categoryFaqs.length === 0) return null;

                    return (
                      <div key={category.id} className="faq-category">
                        <div className="category-header-wrapper">
                          <button
                            className="category-header"
                            onClick={() => toggleCategory(category.id)}
                            aria-expanded={expandedCategory === category.id}
                            aria-controls={`category-${category.id}-content`}
                          >
                            {category.icon && (
                              <span
                                className="category-icon"
                                aria-hidden="true"
                              >
                                {category.icon}
                              </span>
                            )}
                            <h3 className="category-title">
                              {category.category}
                            </h3>
                            <span className="toggle-icon">
                              {expandedCategory === category.id ? "−" : "+"}
                            </span>
                          </button>

                          {canDelete() && (
                            <button
                              className="delete-category-btn"
                              onClick={() => handleDeleteCategory(category.id)}
                            >
                              🗑️
                            </button>
                          )}
                        </div>

                        {expandedCategory === category.id && (
                          <div
                            id={`category-${category.id}-content`}
                            className="category-content"
                          >
                            <Accordion
                              defaultActiveKey="0"
                              className="faq_accordion"
                            >
                              {(showAll[category.id]
                                ? categoryFaqs
                                : categoryFaqs.slice(0, 3)
                              ).map((faq, index) => (
                                <Accordion.Item
                                  key={faq.id}
                                  eventKey={index.toString()}
                                  className="accordion-item"
                                >
                                  <div className="faq-item-header">
                                    <Accordion.Header className="accordion-header">
                                      {faq.question}
                                    </Accordion.Header>

                                    {canDelete() && (
                                      <button
                                        className="delete-faq-btn"
                                        onClick={() =>
                                          handleDeleteFaq(faq.id)
                                        }
                                      >
                                        ❌
                                      </button>
                                    )}
                                  </div>
                                  <Accordion.Body className="accordion-body">
                                    {faq.answer}
                                  </Accordion.Body>
                                </Accordion.Item>
                              ))}
                            </Accordion>

                            {categoryFaqs.length > 3 && (
                              <button
                                className="see-more-btn"
                                onClick={() => toggleShowAll(category.id)}
                              >
                                {showAll[category.id]
                                  ? "Show Less"
                                  : `See More ${category.category} FAQs`}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast.show && <div className="custom-toast">{toast.message}</div>}

      {canAdd() && <AddFaqs />}
    </section>
  );
};

export default PGFaqs;